import { MAX_SHARE_BYTES } from './format';

export interface ShareMetadata {
  id: string;
  title: string;
  bytes: number;
  createdAt: string;
  expiresAt: string | null;
  uploadExpiresAt: string;
  state: 'pending' | 'ready';
}
function parseMetadata(value: unknown): ShareMetadata {
  const item = value as ShareMetadata;
  if (
    !item ||
    !/^[a-f0-9]{64}$/.test(item.id) ||
    typeof item.title !== 'string' ||
    !Number.isSafeInteger(item.bytes) ||
    item.bytes < 1 ||
    item.bytes > MAX_SHARE_BYTES ||
    !['pending', 'ready'].includes(item.state) ||
    !Number.isFinite(Date.parse(item.createdAt)) ||
    (item.expiresAt !== null && !Number.isFinite(Date.parse(item.expiresAt))) ||
    !Number.isFinite(Date.parse(item.uploadExpiresAt))
  )
    throw new Error('Invalid sharing service response.');
  return item;
}
export type ShareExpiry = '14-days' | 'permanent';
export type SharingSessionReader = () => Promise<{ token: string } | null>;
function expiryMode(item: { expiry?: ShareExpiry }): ShareExpiry {
  const mode = item.expiry ?? '14-days';
  if (mode !== '14-days' && mode !== 'permanent')
    throw new Error('Invalid share expiry policy.');
  return mode;
}
function assertExpiryResponse(item: LocalShare, share: ShareMetadata): void {
  if ((share.expiresAt === null) !== (expiryMode(item) === 'permanent'))
    throw new Error('Sharing service returned a different expiry policy.');
}
export interface LocalShare {
  requestId: string;
  secret: string;
  title: string;
  bytes: number;
  share?: ShareMetadata;
  readonly expiry?: ShareExpiry;
}
export interface ShareHistoryPorts {
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  requestId(): string;
  managementSecret(): string;
}
export function createShareHistory(ports: ShareHistoryPorts) {
  const historyKey = 'digitiles.project-shares.v1';
  function readShareHistory(): LocalShare[] {
    try {
      const value = JSON.parse(ports.storage.getItem(historyKey) || '[]');
      return Array.isArray(value)
        ? value
            .filter(
              (item): item is LocalShare =>
                typeof item?.requestId === 'string' &&
                /^[a-f0-9]{64}$/.test(item?.secret) &&
                typeof item?.title === 'string' &&
                Number.isInteger(item?.bytes) &&
                (item.expiry === undefined ||
                  item.expiry === '14-days' ||
                  item.expiry === 'permanent') &&
                (!item.share || /^[a-f0-9]{64}$/.test(item.share.id)),
            )
            .slice(-100)
            .map((item) => ({ ...item, expiry: expiryMode(item) }))
        : [];
    } catch {
      return [];
    }
  }
  function rememberShare(item: LocalShare): void {
    const history = readShareHistory().filter(
      (entry) => entry.requestId !== item.requestId,
    );
    ports.storage.setItem(historyKey, JSON.stringify([...history, item].slice(-100)));
  }
  function forgetShare(requestId: string): void {
    ports.storage.setItem(
      historyKey,
      JSON.stringify(readShareHistory().filter((item) => item.requestId !== requestId)),
    );
  }
  function prepareShare(
    title: string,
    bytes: number,
    expiry: ShareExpiry = '14-days',
  ): LocalShare {
    expiryMode({ expiry });
    if (bytes > MAX_SHARE_BYTES || bytes < 1)
      throw new Error('Projects must be at most 15 MB (15,000,000 bytes).');
    const secret = ports.managementSecret();
    const item = { requestId: ports.requestId(), secret, title, bytes, expiry };
    rememberShare(item); // Recovery capability must persist before any upload starts.
    return item;
  }
  return {
    read: readShareHistory,
    remember: rememberShare,
    forget: forgetShare,
    prepare: prepareShare,
  };
}
export type ShareHistory = ReturnType<typeof createShareHistory>;
export interface SharingTransport {
  fetch: typeof fetch;
  createXHR(): XMLHttpRequest;
}
export class SharingUnavailableError extends Error {
  constructor(readonly status: 404 | 410) {
    super('This share is unavailable, expired or revoked.');
    this.name = 'SharingUnavailableError';
  }
}
export class ProjectSharingClient {
  constructor(
    private readonly endpoint: string | undefined,
    private readonly transport: SharingTransport,
    private readonly history: ShareHistory,
    private readonly session: SharingSessionReader = async () => null,
  ) {}
  private async permanentHeaders(
    item: LocalShare,
    signal?: AbortSignal,
  ): Promise<Record<string, string>> {
    const mode = expiryMode(item);
    const previous = this.history
      .read()
      .find((entry) => entry.requestId === item.requestId);
    if (previous && expiryMode(previous) !== mode)
      throw new Error('The expiry policy of an existing share cannot be changed.');
    if (mode !== 'permanent') return {};
    const session = await this.session();
    signal?.throwIfAborted();
    if (!session?.token)
      throw new Error(
        'An active administrator session is required for permanent shares.',
      );
    return { Authorization: `Bearer ${session.token}` };
  }
  async capabilities(signal?: AbortSignal): Promise<{ permanent: boolean }> {
    const session = await this.session();
    signal?.throwIfAborted();
    if (!session?.token) return { permanent: false };
    const response = await this.check(
      await this.transport.fetch(this.url('/capabilities'), {
        credentials: 'omit',
        cache: 'no-store',
        signal,
        headers: { Authorization: `Bearer ${session.token}` },
      }),
    );
    const value = await response.json();
    return { permanent: value?.permanent === true };
  }
  private url(path: string) {
    if (!this.endpoint)
      throw new Error('Project sharing is not available on this deployment yet.');
    return `${this.endpoint}${path}`;
  }
  private async check(response: Response): Promise<Response> {
    if (!response.ok) {
      if (response.status === 404 || response.status === 410)
        throw new SharingUnavailableError(response.status);
      if (response.status === 401 || response.status === 403)
        throw new Error(
          'Permanent sharing is not authorized for the current session. Sign in with an authorized account and retry.',
        );
      if (response.status === 413)
        throw new Error('This project exceeds the 15 MB sharing limit.');
      if (response.status === 429)
        throw new Error('Sharing limit reached. Please try again later.');
      throw new Error('Sharing could not complete. Check your connection and retry.');
    }
    return response;
  }
  async reserve(
    item: LocalShare,
    signal?: AbortSignal,
    beforeTransfer?: () => void | Promise<void>,
  ): Promise<ShareMetadata> {
    const authorization = await this.permanentHeaders(item, signal);
    signal?.throwIfAborted();
    if (beforeTransfer) await beforeTransfer();
    signal?.throwIfAborted();
    const response = await this.check(
      await this.transport.fetch(this.url('/shares'), {
        method: 'POST',
        credentials: 'omit',
        cache: 'no-store',
        signal,
        headers: {
          ...authorization,
          'Content-Type': 'application/json',
          'X-Share-Management': item.secret,
        },
        body: JSON.stringify({
          requestId: item.requestId,
          title: item.title,
          bytes: item.bytes,
          expiry: expiryMode(item),
        }),
      }),
    );
    const share = parseMetadata(await response.json());
    assertExpiryResponse(item, share);
    item.share = share;
    this.history.remember(item);
    return share;
  }
  async upload(
    item: LocalShare,
    blob: Blob,
    progress: (fraction: number) => void,
    signal: AbortSignal,
    beforeTransfer?: () => void | Promise<void>,
  ): Promise<ShareMetadata> {
    if (blob.size !== item.bytes || blob.size > MAX_SHARE_BYTES)
      return Promise.reject(new Error('Project size changed. Prepare a new share.'));
    const authorization = await this.permanentHeaders(item, signal);
    signal.throwIfAborted();
    if (beforeTransfer) await beforeTransfer();
    signal.throwIfAborted();
    return new Promise((resolve, reject) => {
      const xhr = this.transport.createXHR();
      const abort = () => xhr.abort();
      const finish = () => signal.removeEventListener('abort', abort);
      xhr.open('PUT', this.url(`/shares/${item.share!.id}`));
      for (const [key, value] of Object.entries(authorization))
        xhr.setRequestHeader(key, value);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.setRequestHeader('X-Share-Management', item.secret);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) progress(event.loaded / event.total);
      };
      xhr.onerror = () => {
        finish();
        reject(new Error('Upload interrupted. Retry to reconcile this share.'));
      };
      xhr.onabort = () => {
        finish();
        reject(new DOMException('Cancelled', 'AbortError'));
      };
      xhr.onload = () => {
        finish();
        if (xhr.status < 200 || xhr.status >= 300) {
          reject(
            new Error(
              'Upload could not complete. Retry or prepare a new share if the upload expired.',
            ),
          );
          return;
        }
        try {
          const share = parseMetadata(JSON.parse(xhr.responseText));
          assertExpiryResponse(item, share);
          if (share.state !== 'ready') throw new Error('Upload not finalized.');
          item.share = share;
          this.history.remember(item);
          resolve(share);
        } catch {
          reject(new Error('Unable to confirm upload. Retry to check its status.'));
        }
      };
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) {
        finish();
        reject(new DOMException('Cancelled', 'AbortError'));
        return;
      }
      xhr.send(blob);
    });
  }
  async metadata(id: string, signal?: AbortSignal): Promise<ShareMetadata> {
    if (!/^[a-f0-9]{64}$/.test(id)) throw new Error('Invalid project link.');
    return parseMetadata(
      await (
        await this.check(
          await this.transport.fetch(this.url(`/shares/${id}`), {
            signal,
            credentials: 'omit',
            cache: 'no-store',
          }),
        )
      ).json(),
    );
  }
  async download(id: string, signal: AbortSignal): Promise<Blob> {
    if (!/^[a-f0-9]{64}$/.test(id)) throw new Error('Invalid project link.');
    const response = await this.check(
      await this.transport.fetch(this.url(`/shares/${id}/file`), {
        signal,
        credentials: 'omit',
        cache: 'no-store',
      }),
    );
    const reader = response.body?.getReader();
    if (!reader) throw new Error('Unable to download this project.');
    const chunks: Uint8Array<ArrayBuffer>[] = [];
    let size = 0;
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        size += value.length;
        if (size > MAX_SHARE_BYTES)
          throw new Error('This project exceeds the 15 MB sharing limit.');
        chunks.push(new Uint8Array(value));
      }
    } catch (error) {
      await reader.cancel();
      throw error;
    }
    return new Blob(chunks, { type: 'application/octet-stream' });
  }
  async revoke(item: LocalShare, signal?: AbortSignal): Promise<void> {
    const share = item.share ?? (await this.reserve(item, signal));
    await this.check(
      await this.transport.fetch(this.url(`/shares/${share.id}`), {
        method: 'DELETE',
        signal,
        credentials: 'omit',
        cache: 'no-store',
        headers: { 'X-Share-Management': item.secret },
      }),
    );
    this.history.forget(item.requestId);
  }
}
